/* eslint-disable max-len */
import React, { useEffect, useState } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { connect } from "react-redux";
import { DispatchFunc } from "../../../store/ActionTypes";
import { appInit, signout } from "../../../store/AppActions";
import { UserInfo, UserRoles } from "../../../store/AppTypes";
import { LogoutBtn } from "./keycloaklogoutbtn";
import { ReduxState } from "../../../store/ReduxState";
import { FlexColumn, FlexRow } from "@vwpfs/mary-air-lib/components/air/03-base/Flex/index";
import { ThemeBreakpoints, ThemePalette, ThemeShadowSizes } from "@vwpfs/mary-air-lib/theme/_Types";
import { Container } from "@vwpfs/mary-air-lib/components/air/03-base/Container";
import { Grid } from "@vwpfs/mary-air-lib/components/air/03-base/Grid";
import { Div } from "@vwpfs/mary-air-lib/components/air/03-base/Div";
import { Alignment, ButtonsWrapper, Orientation } from "@vwpfs/mary-air-lib/components/air/06-molecules/ButtonsWrapper";
import { RTE } from "@vwpfs/mary-air-lib/components/air/07-organisms/RTE";
import { NavbarStack, NavbarStackContent } from "@vwpfs/mary-air-lib/components/air/07-organisms/Navbar/Stack";
import { NavbarTitle } from "@vwpfs/mary-air-lib/components/air/07-organisms/Navbar/Title";
import { Navbar } from "@vwpfs/mary-air-lib/components/air/07-organisms";
import { RemoteScope } from "../../../store/RemoteTypes";
import { remoteTrigger } from "../../../store/RemoteActions";

interface OwnProps {
    onAuthenticating?: React.ReactNode;
    onNotAllowed?: React.ReactNode;
}

interface DispatchProps {
    triggerInit: (userInfo?: UserInfo) => void;
    getAppversion: () => void;
    signout: () => void;
}

interface StateProps {
    token?: string;
}

type Props = React.PropsWithChildren<OwnProps & DispatchProps & StateProps>;

export const mapDispatchToProps = (dispatch: DispatchFunc): DispatchProps => ({
    triggerInit: (userInfo?: UserInfo) => dispatch(appInit(userInfo)),
    getAppversion: () => dispatch(remoteTrigger(RemoteScope.VERSION, undefined)),
    signout: () => dispatch(signout()),
});

const mapStateToProps = (s: ReduxState): StateProps => ({
    token: s.prop("user")?.isPresent() ? s.prop("user").get()?.token : undefined,
});


export const KeycloakAuthenticatedComp: React.FC<Props> = (props) => {
    const [hasError, setHasError] = useState(false);
    const { keycloak } = useKeycloak();

    const handleFocusLogout = React.useCallback(() => {
        keycloak?.logout({ redirectUri: import.meta.env.VITE_APP_URL }).catch(() => {
            console.warn("Iris: Logout after session loss failed");
        });
    }, [keycloak]);

    const checkSessionOnTabActivation = React.useCallback((reason: string) => {
        console.info(`Iris: Session check triggered by ${reason}`);
        if (document.visibilityState !== "visible") {
            return;
        }
        if (!keycloak) {
            console.warn("Iris: Session check skipped because keycloak is unavailable");
            return;
        }
        if (!keycloak.authenticated || !keycloak.token || !keycloak.refreshToken) {
            console.info("Iris: Session check skipped because keycloak is not fully authenticated yet");
            return;
        }
        // -1 forces a token refresh regardless of expiry, so a dead session (e.g. logged out
        // in another tab) is detected immediately rather than waiting for the token to expire.
        keycloak.updateToken(-1).catch(() => {
            console.warn("Iris: Session lost in another tab, logging out");
            handleFocusLogout();
        });
    }, [keycloak, handleFocusLogout]);

    useEffect(() => {
        if (keycloak?.authenticated) {
            checkSessionOnTabActivation("initial mount");
        }
    }, [keycloak, checkSessionOnTabActivation]);

    useEffect(() => {
        if (keycloak?.token) {
            setHasError(false);
            const userRole = Object.values(UserRoles).find(r => keycloak?.resourceAccess?.[import.meta.env.VITE_IRIS_CLIENTID ?? ""]?.roles.includes(r));
            props.triggerInit({
                token: keycloak?.token,
                keycloak: keycloak,
                userName: (keycloak?.idTokenParsed as {name?: string})?.name  ?? "",
                userRole,
                email: (keycloak?.idTokenParsed as {email?: string})?.email  ?? "",
            });
            props.getAppversion();
        }

        if (keycloak) {
            keycloak.onAuthError = () => {
                setHasError(true);
            };
            keycloak.onTokenExpired = () => {
                console.warn("App:focus:keycloak:onTokenExpired: Update token");
                keycloak
                    .updateToken(50)
                    .then((_d) => {
                        console.info("Iris: Token updated");
                    })
                    .catch(() => {
                        console.warn("Iris: Token not updated");
                    });
            };
        }
        if (keycloak?.authenticated) {
            console.log("Claude has verified that you are authenticated with IRIS.");
        } else {
            console.warn("Claude detects that you are not authenticated.");
            if (props.token) {
                props.signout();
            }
        }

        const handleVisibilityChange = () => checkSessionOnTabActivation("visibilitychange");
        const handleWindowFocus = () => checkSessionOnTabActivation("window focus");

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleWindowFocus);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", handleWindowFocus);
        };
    }, [keycloak?.token, checkSessionOnTabActivation]);

    const resource_access =
    keycloak?.tokenParsed?.resource_access && keycloak?.tokenParsed?.azp
        ? keycloak?.tokenParsed?.resource_access?.[keycloak?.tokenParsed?.azp]
        : undefined;

    const hasValidUserRole = (resources?: { roles: string[] }) =>
        resources?.roles.some((role) => Object.values(UserRoles).includes(role as UserRoles));

    const renderAuthContent = (): React.ReactNode => {
        if (keycloak?.authenticated && hasValidUserRole(resource_access) && props.children) {
            return React.cloneElement(
                props.children as React.ReactElement<{
                    authenticated?: boolean;
                }>,
                { authenticated: keycloak?.authenticated },
            );
        }
        if (keycloak?.authenticated && !hasValidUserRole(resource_access) && props.onNotAllowed) {
            return props.onNotAllowed;
        }
        if (props.onAuthenticating) {
            return props.onAuthenticating;
        }
        return null;
    };

    return (
        <>
            {hasError ? (
                <FlexColumn isPage theme={{palette: ThemePalette.CONTRAST_SECONDARY}} >
                    <FlexRow isFixed>
                        <Container
                            theme={{
                                padding: { "": { y: 3 }, "sm": { y: 4 } },
                            }}
                        >
                            <Div className="scl-b-row">
                                <Grid
                                    offset={{ xs: 0, md: 3 }}
                                    size={{ xs: 12, md: 6 }}
                                    theme={{
                                        padding: {
                                            "": { b: 3, t: 1 },
                                            "md": { b: 4, t: 2 },
                                        },
                                    }}
                                >
                                    <Div theme={{shadow: ThemeShadowSizes.TINY}}>
                                        <FlexColumn>
                                            <FlexRow isFixed>
                                                <Navbar
                                                    theme={{
                                                        palette: ThemePalette.CONTRAST_TERTIARY,
                                                    }}
                                                >
                                                    <NavbarStack>
                                                        <NavbarStackContent>
                                                            <NavbarTitle>
                                                                Authentication failed
                                                            </NavbarTitle>
                                                        </NavbarStackContent>
                                                    </NavbarStack>
                                                </Navbar>
                                            </FlexRow>
                                            <FlexRow
                                                theme={{
                                                    palette: ThemePalette.CONTRAST_PRIMARY,
                                                }}
                                            >
                                                <Container
                                                    theme={{
                                                        padding: {
                                                            "": { b: 3, t: 3 },
                                                            "md": { b: 4, t: 4 },
                                                        },
                                                    }}
                                                >
                                                    <RTE>Something went wrong, we can&apos;t seem to authenticate you.</RTE>
                                                </Container>
                                            </FlexRow>
                                            <FlexRow isFixed>
                                                <FlexColumn
                                                    theme={{
                                                        palette: ThemePalette.CONTRAST_TERTIARY,
                                                    }}
                                                >
                                                    <Container
                                                        theme={{
                                                            padding: { "": { y: 2 } },
                                                        }}
                                                    >
                                                        <ButtonsWrapper
                                                            orientations={{ [ThemeBreakpoints.XS]: Orientation.HORIZONTAL}}
                                                            alignment={Alignment.CENTER}
                                                        >
                                                            <LogoutBtn noIcon />
                                                        </ButtonsWrapper>
                                                    </Container>
                                                </FlexColumn>
                                            </FlexRow>
                                        </FlexColumn>
                                    </Div>
                                </Grid>
                            </Div>
                        </Container>
                    </FlexRow>
                </FlexColumn>) : renderAuthContent()}
        </>
    );
};

/**
 *
 */
export const KeycloakAuthenticated = connect(
    mapStateToProps,
    mapDispatchToProps,
)(KeycloakAuthenticatedComp);

